/**
 * Navigation Helpers
 *
 * Role-based navigation logic for different account types.
 * Ensures users only see relevant menu items for their role.
 */

import { AccountRole } from '../types/accounts'
import {
  LayoutDashboard,
  BookOpen,
  Trophy,
  Settings,
  Users,
  GraduationCap,
  Lightbulb,
  ShieldCheck,
  Code,
  UserCog,
  LucideIcon,
  Home,
} from 'lucide-react'
export interface NavigationItem {
  id: string
  label: string
  icon: LucideIcon
  isDev?: boolean
}

// ── Static navigation arrays (memoized — no new arrays on every call) ────────

const LEARNER_NAV: NavigationItem[] = [
  { id: 'dashboard', label: 'Home', icon: Home },
  { id: 'modules', label: 'Vakken', icon: BookOpen },
  { id: 'progress', label: 'Voortgang', icon: Trophy },
  { id: 'learningLab', label: 'Oefenlab', icon: Lightbulb },
  { id: 'settings', label: 'Instellingen', icon: Settings },
]

const PARENT_NAV: NavigationItem[] = [
  { id: 'parent', label: 'Mijn Kinderen', icon: Users },
  { id: 'settings', label: 'Instellingen', icon: Settings },
]

const TEACHER_NAV: NavigationItem[] = [
  { id: 'teacher', label: 'Mijn Klas', icon: GraduationCap },
  { id: 'settings', label: 'Instellingen', icon: Settings },
]

const ADMIN_NAV: NavigationItem[] = [
  { id: 'dashboard', label: 'Beheer', icon: ShieldCheck },
  { id: 'settings', label: 'Instellingen', icon: Settings },
]

const DEFAULT_NAV: NavigationItem[] = [
  { id: 'settings', label: 'Instellingen', icon: Settings },
]

/**
 * Get navigation items based on account role.
 * Returns static arrays (no new allocations per call).
 *
 * Parent and teacher roles now include Voortgang — the
 * `requiresViewingLearnerSelection` gate in App.tsx handles
 * the case where no learner is selected yet.
 */
export function getNavigationItemsForRole(role: AccountRole): NavigationItem[] {
  switch (role) {
    case 'learner':
      return LEARNER_NAV
    case 'parent':
      return PARENT_NAV
    case 'teacher':
      return TEACHER_NAV
    case 'admin':
      return ADMIN_NAV
    default:
      return DEFAULT_NAV
  }
}
/**
 * Get default view for account role
 */
export function getDefaultViewForRole(role: AccountRole): string {
  switch (role) {
    case 'learner':
      return 'dashboard'
    case 'parent':
      return 'parent'
    case 'teacher':
      return 'teacher'
    case 'admin':
      return 'admin'
    default:
      return 'settings'
  }
}
/**
 * Check if a view is accessible for a given role
 */
export function isViewAccessibleForRole(
  view: string,
  role: AccountRole,
): boolean {
  const navItems = getNavigationItemsForRole(role)
  return navItems.some((item) => item.id === view)
}
